// FRONT-3249: the pure half of the model picker a GenAI Hub workspace sees.
// Everything here turns the Hub's facts into words and choices a person in a
// company understands — what a model is good for, how good, fast and dear it
// is, and where their data is processed — and never shows a gateway alias, a
// lifecycle code or a score as a raw number. No React, so every rule is tested.

import { deriveRegion, type Region } from '~/components/model-badges';
import type { PickerModelEntry } from './model-picker-logic';
import { legacyNames } from './model-versions';

/** One place a model runs: a cloud in a region. Picking a place picks its alias. */
export interface ModelPlace {
  id: string;
  cloud: string;
  region: Region | null;
}

/** One model, with every place it runs, in the Hub's order. */
export interface PickerModelGroup {
  key: string;
  name: string;
  maker: string | null;
  goodFor: string | null;
  /** The scores of the group's first place: the same model scores alike everywhere. */
  quality: number | null;
  speed: number | null;
  cost: number | null;
  recommended: boolean;
  agents: boolean;
  status: 'preview' | 'retiring' | null;
  /** An older version of a model line that has a newer one in this list (`model-versions.ts`). */
  legacy: boolean;
  places: ModelPlace[];
  /** The Hub's position of the group's first place, for a stable order. */
  hubIndex: number;
}

export type PickerSort = 'recommended' | 'quality' | 'speed' | 'price';
export const PICKER_SORTS: readonly { id: PickerSort; label: string }[] = [
  { id: 'recommended', label: 'Recommended' },
  { id: 'quality', label: 'Best quality' },
  { id: 'speed', label: 'Fastest' },
  { id: 'price', label: 'Lowest price' },
];

const REGIONS = new Set<Region>(['eu', 'us', 'cn', 'de', 'global', 'multi']);

/** What the Hub's tier means, in a person's words. An unknown tier says nothing. */
const GOOD_FOR: Record<string, string> = {
  everyday: 'Quick everyday tasks',
  balanced: 'A balance of quality and speed',
  'deep-work': 'Complex, in-depth work',
};

/** Cloud names a person recognises, from the Hub's provider labels. */
export function cloudName(label: string | null | undefined): string | null {
  if (!label) return null;
  const l = label.toLowerCase();
  if (l.includes('bedrock') || l === 'aws') return 'AWS';
  if (l.includes('vertex') || l.includes('google')) return 'Google Cloud';
  if (l.includes('azure')) return 'Microsoft Azure';
  if (l.includes('stackit')) return 'STACKIT';
  return label;
}

/** Where a place processes data, as a person says it. */
export function regionName(region: Region | null): string | null {
  if (!region) return null;
  return { eu: 'EU', de: 'Germany', us: 'US', cn: 'China', global: 'Global', multi: 'Several regions' }[region];
}

function placeOf(m: PickerModelEntry): ModelPlace {
  const residency = m.hub?.residency?.toLowerCase();
  const region = deriveRegion(m.id, residency && REGIONS.has(residency as Region) ? (residency as Region) : undefined) ?? null;
  return { id: m.id, cloud: cloudName(m.hub?.providerLabel) ?? 'Unknown cloud', region };
}

function statusOf(lifecycle: string | null | undefined): PickerModelGroup['status'] {
  const l = lifecycle?.toLowerCase();
  if (l === 'beta' || l === 'evaluation' || l === 'announced') return 'preview';
  if (l === 'deprecated' || l === 'retired') return 'retiring';
  return null;
}

/** Models grouped by name (case and spacing ignored), each with its places, in the Hub's order. */
export function groupPickerModels(models: readonly PickerModelEntry[]): PickerModelGroup[] {
  const groups = new Map<string, PickerModelGroup>();
  models.forEach((m, hubIndex) => {
    const name = (m.hub?.displayName ?? m.displayName).trim();
    const key = name.toLowerCase().replace(/\s+/g, ' ');
    const existing = groups.get(key);
    if (existing) {
      existing.places.push(placeOf(m));
      existing.recommended ||= !!m.hub?.recommended;
      existing.agents ||= !!m.hub?.agentCompatible;
      return;
    }
    const s = m.hub?.scores ?? {};
    groups.set(key, {
      key,
      name,
      maker: m.hub?.developerLabel ?? null,
      goodFor: (m.hub?.tier && GOOD_FOR[m.hub.tier]) || null,
      quality: s.quality ?? null,
      speed: s.speed ?? null,
      cost: s.cost ?? null,
      recommended: !!m.hub?.recommended,
      agents: !!m.hub?.agentCompatible,
      status: statusOf(m.hub?.lifecycleStatus),
      legacy: false,
      places: [placeOf(m)],
      hubIndex,
    });
  });
  const all = [...groups.values()];
  // "Older" is said against the whole list, so it is decided once the groups exist.
  const legacy = legacyNames(all.map((g) => g.name));
  for (const g of all) g.legacy = legacy.has(g.key);
  return all;
}

/** 0–5 filled segments for a 0–100 score; null when the Hub has not published one. */
export function meterSegments(score: number | null): number | null {
  return score === null ? null : Math.max(1, Math.min(5, Math.round(score / 20)));
}

/** The Hub's cost score is higher when cheaper; a person reads price as €–€€€€. */
export function priceLevel(cost: number | null): 1 | 2 | 3 | 4 | null {
  if (cost === null) return null;
  if (cost >= 75) return 1;
  if (cost >= 50) return 2;
  if (cost >= 25) return 3;
  return 4;
}

export interface PickerFilters {
  region: Region | 'all';
  maker: string | 'all';
  /** Only the rows the person starred. Absent reads as off. */
  favouritesOnly?: boolean;
}

/** The regions and makers present in the list, for the filter chips. Germany counts as EU too. */
export function filterOptions(groups: readonly PickerModelGroup[]): { regions: Region[]; makers: string[] } {
  const regions = new Set<Region>();
  const makers = new Set<string>();
  for (const g of groups) {
    if (g.maker) makers.add(g.maker);
    for (const p of g.places) if (p.region) regions.add(p.region);
  }
  if (regions.has('de')) regions.add('eu');
  const order: Region[] = ['eu', 'de', 'us', 'global', 'multi', 'cn'];
  return { regions: order.filter((r) => regions.has(r)), makers: [...makers].sort((a, b) => a.localeCompare(b)) };
}

const inRegion = (p: ModelPlace, region: Region | 'all') => region === 'all' || p.region === region || (region === 'eu' && p.region === 'de');

/**
 * Apply the filters and the sort. A region filter keeps only the places in
 * that region and the favourites filter only the starred ones (a model with none
 * drops out); an unpublished score sorts last; ties keep the Hub's order.
 */
export function visibleGroups(
  groups: readonly PickerModelGroup[],
  filters: PickerFilters,
  sort: PickerSort,
  starred: readonly string[] = [],
): PickerModelGroup[] {
  const stars = new Set(starred);
  const shown = groups
    .filter((g) => filters.maker === 'all' || g.maker === filters.maker)
    .map((g) => ({
      ...g,
      places: g.places.filter((p) => inRegion(p, filters.region) && (!filters.favouritesOnly || stars.has(p.id))),
    }))
    .filter((g) => g.places.length > 0);
  const key = (g: PickerModelGroup): number => {
    if (sort === 'quality') return g.quality ?? -1;
    if (sort === 'speed') return g.speed ?? -1;
    if (sort === 'price') return g.cost ?? -1;
    return g.recommended ? 1 : 0;
  };
  return shown.sort((a, b) => key(b) - key(a) || a.hubIndex - b.hubIndex);
}

/** One line of the picker: a model on ONE cloud, so "Claude Haiku 5.5 on AWS, EU" is a row of its own. */
export interface PickerRow {
  group: PickerModelGroup;
  place: ModelPlace;
}

/** The rows to show, from {@link visibleGroups}: every place of a model becomes its own row, side by side in the Hub's order. */
export function pickerRows(groups: readonly PickerModelGroup[]): PickerRow[] {
  return groups.flatMap((group) => group.places.map((place) => ({ group, place })));
}

/** "AWS, EU" — where a place runs, as a person says it. */
export function placeLabel(place: ModelPlace): string {
  return [place.cloud, regionName(place.region)].filter(Boolean).join(', ');
}

/** What the chat window spends outside the chooser: its header bar, and the action row and paddings below the box. */
const CHAT_HEADER_PX = 60;
const CHAT_BELOW_PX = 80;
const CHOOSER_MIN_PX = 280;

/**
 * How tall the "All models" chooser is inside the composer box. The box grows to
 * the chat window's body — the window's height less its header and what sits
 * around the box — so the chooser takes what is left after the box's own chrome
 * (the button row). Without a chat window (any other surface) it is most of the
 * viewport. Never shorter than a few rows.
 */
export function allModelsHeight({ viewport, chat, boxChrome }: { viewport: number; chat: number | null; boxChrome: number }): number {
  const room = chat === null ? viewport * 0.7 : chat - CHAT_HEADER_PX - CHAT_BELOW_PX;
  return Math.max(CHOOSER_MIN_PX, Math.round(room - boxChrome));
}
